import { ListProps, Box, List, chakra } from "@chakra-ui/react";
import { ReactElement, useMemo } from "react";
import { Filter } from "../../DataSource/config";
import { checkExhausted, filterAndMap, replaceItem } from "../../core/utils";
import ComboModeBox from "../ComboModeBox";
import SidebarGroup from "./SidebarGroup";

interface SidebarProps extends Omit<ListProps, "onChange"> {
  filters: Array<Filter>;
  onChange: (filters: Array<Filter>) => void;
}

export default function Sidebar(props: SidebarProps): ReactElement {
  const { filters, onChange, bg = "white", ...rest } = props;

  const selectedSet = useMemo(
    () =>
      new Set(
        filterAndMap(filters, (filter) =>
          filter.options.every(({ value }) => filter.value.includes(value))
            ? filter.column
            : null
        )
      ),
    [filters]
  );

  const content = filters.map((filter, i) => (
    <SidebarFilter
      key={i}
      filter={filter}
      isSelected={selectedSet.has(filter.column)}
      onChange={(newFilter) => onChange(replaceItem(filters, i, newFilter))}
    />
  ));

  return (
    <List id="filters" bg={bg} {...rest}>
      {content}
    </List>
  );
}

interface SidebarFilterProps {
  filter: Filter;
  isSelected: boolean;
  onChange: (filter: Filter) => void;
}

function SidebarFilter(props: SidebarFilterProps): ReactElement {
  const { filter, isSelected, onChange } = props;

  switch (filter.type) {
    case "Standard":
      return (
        <SidebarGroup label={filter.column} className="standard-filter-item">
          <ComboModeBox
            value={filter.value}
            options={filter.options}
            containAll={filter.containAll}
            onChange={(value) => onChange({ ...filter, value })}
            onContainAllChange={(containAll) =>
              onChange({ ...filter, containAll })
            }
          />
        </SidebarGroup>
      );

    case "Radio":
      return (
        <SidebarGroup
          label={filter.column}
          className="radio-filter-item"
          left={
            <Box
              onClick={(evt) => {
                evt.stopPropagation();
                onChange({
                  ...filter,
                  value: isSelected
                    ? []
                    : filter.options.map(({ value }) => value),
                });
              }}
            >
              {/* HACK BECAUSE THE RADIO CHECKED state WASN'T UPDATING */}
              <chakra.input
                type="radio"
                checked={true}
                readOnly={true}
                style={{ accentColor: "var(--chakra-colors-gray-600)" }}
                visibility={isSelected ? undefined : "hidden"}
                display={isSelected ? undefined : "none"}
              />
              <chakra.input
                type="radio"
                checked={false}
                readOnly={true}
                style={{ accentColor: "var(--chakra-colors-gray-600)" }}
                visibility={isSelected ? "hidden" : undefined}
                display={isSelected ? "none" : undefined}
              />
            </Box>
          }
        >
          <ComboModeBox
            value={filter.value}
            options={filter.options}
            containAll={filter.containAll}
            onChange={(value) => onChange({ ...filter, value })}
            onContainAllChange={(containAll) =>
              onChange({ ...filter, containAll })
            }
          />
        </SidebarGroup>
      );

    default:
      return checkExhausted(filter);
  }
}
